(function () {
  const bannerSelector = '[data-meta-iab-open-browser-banner]';
  const handoffMountSelector = '[data-cart-iab-handoff-mount]';

  const metaUaTokenPattern =
    /(?:FBAN|FBAV|FB_IAB|FB4A|FBIOS|FBIAB|FBBV|FBDV|Messenger|Instagram|Threads|\[FB)/i;
  const metaUaBracketPattern = /\[[^\]]*FB[A-Z0-9_]*\//i;

  function getUserAgent() {
    return navigator.userAgent || '';
  }

  function hasFacebookReferrer() {
    try {
      const ref = document.referrer || '';
      return /(^|\/\/)(?:[^/]+\.)?(?:facebook|instagram|fb)\.com/i.test(ref);
    } catch (error) {
      return false;
    }
  }

  function isIosInAppWebView(ua) {
    if (!/iPhone|iPad|iPod/i.test(ua)) return false;
    if (/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return false;
    if (/Safari/i.test(ua) && /Version\//i.test(ua)) return false;
    return /AppleWebKit/i.test(ua) && /Mobile\//i.test(ua);
  }

  function isAndroidInAppWebView(ua) {
    return /Android/i.test(ua) && /\bwv\b/i.test(ua);
  }

  function isMetaInAppBrowser() {
    try {
      const host = window.location.hostname || '';
      if (
        (host === '127.0.0.1' || host === 'localhost') &&
        new URLSearchParams(window.location.search).has('meta_iab_test')
      ) {
        return true;
      }
    } catch (error) {
      /* ignore */
    }

    const ua = getUserAgent();

    if (metaUaTokenPattern.test(ua) || metaUaBracketPattern.test(ua)) {
      return true;
    }

    if (isIosInAppWebView(ua) && hasFacebookReferrer()) {
      return true;
    }

    if (isAndroidInAppWebView(ua) && (metaUaTokenPattern.test(ua) || hasFacebookReferrer())) {
      return true;
    }

    return false;
  }

  function isAndroid() {
    return /Android/i.test(getUserAgent());
  }

  function isIos() {
    return /iPhone|iPad|iPod/i.test(getUserAgent());
  }

  function buildAndroidIntentUrl(url) {
    const normalizedUrl = new URL(url, window.location.origin);
    const protocol = normalizedUrl.protocol.replace(':', '');
    const path = `${normalizedUrl.host}${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
    return `intent://${path}#Intent;scheme=${protocol};action=android.intent.action.VIEW;end`;
  }

  function buildCartPermalink(items) {
    if (!Array.isArray(items) || !items.length) return null;

    const parts = items
      .map((item) => {
        const variantId = Number(item?.variant_id || item?.id);
        const quantity = Number(item?.quantity || 0);
        if (!Number.isFinite(variantId) || variantId <= 0) return null;
        if (!Number.isFinite(quantity) || quantity <= 0) return null;
        return `${variantId}:${quantity}`;
      })
      .filter(Boolean);

    if (!parts.length) return null;
    return `/cart/${parts.join(',')}`;
  }

  async function getBrowserHandoffUrl() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return window.location.href;

      const cart = await response.json();
      const permalinkPath = buildCartPermalink(cart?.items);
      if (!permalinkPath) return window.location.href;

      return new URL(permalinkPath, window.location.origin).toString();
    } catch (error) {
      console.warn('Failed to build cart handoff URL.', error);
      return window.location.href;
    }
  }

  function getFallbackMessage() {
    if (isIos()) {
      return 'Tap the menu (⋯) in Facebook, then choose Open in Browser or Open in Safari. Apple Pay works in Safari, not inside the Facebook app.';
    }

    if (isAndroid()) {
      return 'Tap the menu (⋯) in Facebook or Instagram, then choose Open in browser. Google Pay may not work inside the in-app browser.';
    }

    return 'Use the in-app browser menu (⋯) and choose Open in browser.';
  }

  async function copyUrlToClipboard(url) {
    if (!navigator.clipboard?.writeText) return false;

    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch (error) {
      console.warn('Failed to copy cart URL for browser handoff.', error);
      return false;
    }
  }

  async function handleMetaIosHandoff(url, banner) {
    if (navigator.share) {
      try {
        await navigator.share({ url, title: document.title });
        updateHint(
          banner,
          'If Safari did not open, tap ⋯ in Facebook and choose Open in Browser or Open in Safari.'
        );
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    const copied = await copyUrlToClipboard(url);
    const prefix = copied ? 'Store link copied. ' : '';
    updateHint(banner, `${prefix}${getFallbackMessage()}`);
  }

  function handleMetaAndroidHandoff(url, banner) {
    const intentUrl = buildAndroidIntentUrl(url);
    let leftPage = false;

    const onVisibilityChange = function () {
      if (document.visibilityState === 'hidden') {
        leftPage = true;
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange, { once: true });

    window.setTimeout(async function () {
      document.removeEventListener('visibilitychange', onVisibilityChange);

      if (leftPage) return;

      const copied = await copyUrlToClipboard(url);
      const prefix = copied ? 'Store link copied. ' : '';
      updateHint(banner, `${prefix}${getFallbackMessage()}`);
    }, 1200);

    window.location.href = intentUrl;
  }

  function updateHint(banner, message) {
    const hint = banner.querySelector('[data-meta-iab-browser-hint]');
    if (!hint) return;

    hint.textContent = message;
    hint.hidden = false;
  }

  async function handleOpenInBrowserClick(event) {
    event.preventDefault();

    const trigger = event.currentTarget;
    const banner = trigger.closest(bannerSelector);
    if (!banner) return;

    const handoffUrl = await getBrowserHandoffUrl();

    if (isIos()) {
      await handleMetaIosHandoff(handoffUrl, banner);
      return;
    }

    if (isAndroid()) {
      handleMetaAndroidHandoff(handoffUrl, banner);
      return;
    }

    const copied = await copyUrlToClipboard(handoffUrl);
    const prefix = copied ? 'Store link copied. ' : '';
    updateHint(banner, `${prefix}${getFallbackMessage()}`);
  }

  function createBanner() {
    const banner = document.createElement('div');
    banner.className = 'cart-meta-iab-banner';
    banner.dataset.metaIabOpenBrowserBanner = '';
    banner.innerHTML = `
      <p class="cart-meta-iab-banner__copy">
        Apple Pay and Google Pay may not work inside the Facebook or Instagram in-app browser.
      </p>
      <button type="button" class="cart-meta-iab-banner__button">
        Open in your default browser for Apple Pay / Google Pay
      </button>
      <p class="cart-meta-iab-banner__hint" data-meta-iab-browser-hint hidden></p>
    `;

    const button = banner.querySelector('.cart-meta-iab-banner__button');
    button?.addEventListener('click', handleOpenInBrowserClick);

    return banner;
  }

  function removeCartAcceleratedCheckout() {
    const selectors =
      'shopify-accelerated-checkout-cart, .additional-checkout-buttons, .cart__additional-checkout-buttons, .shopify-payment-button';
    const roots = document.querySelectorAll(
      '.cart-drawer__footer-sticky, body.template-cart .cart-page__summary, body.template-cart .cart-summary'
    );

    roots.forEach(function (root) {
      root.querySelectorAll(selectors).forEach(function (node) {
        node.remove();
      });
    });

    document.querySelectorAll('.cart__ctas ' + selectors).forEach(function (node) {
      node.remove();
    });
  }

  function syncBanners() {
    const metaIab = isMetaInAppBrowser();
    document.documentElement.classList.toggle('meta-iab-browser', metaIab);

    if (metaIab) {
      removeCartAcceleratedCheckout();
    }

    document.querySelectorAll(bannerSelector).forEach(function (banner) {
      const mount = banner.closest(handoffMountSelector);
      if (!metaIab || !mount) {
        banner.remove();
      }
    });

    if (!metaIab) return;

    document.querySelectorAll(handoffMountSelector).forEach(function (mount) {
      if (mount.querySelector(bannerSelector)) return;
      mount.appendChild(createBanner());
    });
  }

  let syncQueued = false;

  function queueSync() {
    if (syncQueued) return;
    syncQueued = true;

    window.requestAnimationFrame(function () {
      syncQueued = false;
      syncBanners();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueSync, { once: true });
  } else {
    queueSync();
  }

  const observer = new MutationObserver(queueSync);
  observer.observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('pageshow', queueSync);

  document.addEventListener(
    'dialog:open',
    function (event) {
      if (!event.target?.closest?.('cart-drawer-component')) return;
      queueSync();
      window.setTimeout(queueSync, 150);
      window.setTimeout(queueSync, 600);
    },
    true
  );
})();
